import React, { useEffect, useRef, useState } from 'react';

/* ══════════════════════════════════════════════════════════════════════════
 * THE RAIL THAT MOVES ON ITS OWN, AND THE ARROWS
 *
 * Al, 23 September 2026: *"the carousel doesn't work now. So it's not
 * carouseling. None of the daily deals, last minute specials, happy hours are
 * carouseling."*
 *
 * And before that, 29 August 2026: *"are the carousels working. they should be
 * moving everytime i open the homepage and the view all and also the explore
 * pages. this is not hardwired in."*
 *
 * ── ⚠️ WHAT REACT ACTUALLY HAD ─────────────────────────────────────────
 *
 * A `div.carousel` with `overflow-x: auto`. It scrolled if you pushed it and
 * did nothing otherwise — no arrows, no drift, and nothing endless. **A row of
 * cards that never moves reads as a row of cards that cannot**, which is
 * exactly what Al reported.
 *
 * This is `Eleater Prototype/eleat_carousel.js` ported. Every rule below is
 * its rule, and each one is there because it was got wrong first:
 *
 *   · it advances one card every 4.2 seconds
 *   · ONLY the rail on screen moves — Al, 8 Sep: *"if he's looking at daily
 *     deals, you can hardly see happy hour, then happy hour should stop"*
 *   · it stops for good, on that rail, the moment the Eleater drags it
 *     SIDEWAYS — a vertical swipe is somebody scrolling past, not taking over
 *   · it never runs while the tab is hidden
 *   · it is endless: the card that scrolls off the left is moved to the back
 *     and the scroll position pulled back by its width in the same instant,
 *     so the row never hits a wall and never flies back to the start.
 *     Al: *"it doesn't go all the way back. It just goes to one again."*
 *
 * ⚠️ AND THE ORDER IS SOMEBODY ELSE'S JOB. `rotate.js` decides who is in slot
 * one. This file only scrolls what it is given. The prototype keeps the same
 * line: *"one scrolls a rail, the other arranges it before anybody scrolls
 * anything."*
 * ══════════════════════════════════════════════════════════════════════════ */

const EVERY_MS = 4200;      /* the prototype's own interval */
const CARD_GAP = 13;        /* .carousel { gap: 13px } in eleat.css */

export function Carousel({ children, endcard }) {
  const wrap = useRef(null);
  const rail = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  useEffect(() => {
    const el = rail.current;
    const box = wrap.current;
    if (!el || !box) return undefined;

    let stopped = false;
    let timer = null;
    let onScreen = false;
    /* ⚠️⚠️ THIS COUNTER BELONGS TO THIS RAIL AND NOTHING ELSE. The prototype
     * put the "I moved myself" marker on `window`, so Home's four rails shared
     * one: when Happy Hour moved itself it raised Game Day's flag, Game Day
     * checked its own — still down — decided a person had grabbed it, and
     * stopped for the session. Three rows out of four could kill themselves.
     * It is a ref inside one component here, which is the same fix. */
    let selfMoving = 0;
    let held = false;
    let wasX = el.scrollLeft;

    function selfMove(fn) {
      selfMoving += 1;
      try { fn(); } finally {
        setTimeout(() => { if (selfMoving > 0) selfMoving -= 1; }, 900);
      }
    }

    function stop() {
      stopped = true;
      if (timer) { clearInterval(timer); timer = null; }
    }

    function step() {
      const card = el.querySelector('.card');
      return card ? card.offsetWidth + CARD_GAP : Math.round(el.clientWidth * 0.8);
    }

    function sync() {
      const max = el.scrollWidth - el.clientWidth - 2;
      setCanPrev(el.scrollLeft > 2);
      setCanNext(max > 0 && el.scrollLeft < max);
    }

    /* ⚠️ SNAPPING OFF FOR THAT INSTANT. `.carousel` is `scroll-snap-type: x
     * mandatory`; changing scrollLeft while snapping is live makes the browser
     * glide to the nearest card, which is the jump this exists to avoid.
     * ⚠️ AND THE ORIGINAL IS CAPTURED ONCE. The prototype saved whatever it
     * found on every call, so a second call saved 'none' and put 'none' back —
     * snapping stayed off for the session and cards settled halfway. */
    let snapWas = null;
    let snapDepth = 0;
    function silently(fn) {
      if (snapDepth === 0) snapWas = el.style.scrollSnapType;
      snapDepth += 1;
      el.style.scrollSnapType = 'none';
      try { selfMove(fn); } finally {
        const release = () => {
          snapDepth -= 1;
          if (snapDepth <= 0) { snapDepth = 0; el.style.scrollSnapType = snapWas || ''; }
        };
        if (window.requestAnimationFrame) window.requestAnimationFrame(release);
        else setTimeout(release, 0);
      }
    }

    const isCard = (n) => n.nodeType === 1 && !/\b(endcard|more-card|viewall|view-all)\b/
      .test(String(n.getAttribute('class') || ''));
    const cardsOf = () => Array.prototype.filter.call(el.children, isCard);
    const tailOf = () => Array.prototype.filter.call(el.children, (n) => !isCard(n))[0];

    /* ⚠️ THE RAIL HAS NO END, SO THERE IS NOTHING TO REWIND FROM. */
    function keepFed() {
      if (cardsOf().length < 3) return;
      const by = step();
      let guard = 0;
      while (el.scrollLeft > by * 1.5 && guard < 40) {
        guard += 1;
        const first = cardsOf()[0];
        if (!first || first.parentNode !== el) break;
        silently(() => {
          const x = el.scrollLeft;
          el.removeChild(first);
          /* ⚠️ BEFORE THE TAIL, NOT AFTER IT. The "View all" card at the end
             of a rail is furniture, not a venue, and stays pinned to the end. */
          const tail = tailOf();
          if (tail) el.insertBefore(first, tail); else el.appendChild(first);
          el.scrollLeft = x - by;
        });
      }
      guard = 0;
      while (el.scrollLeft < by * 0.5 && guard < 40) {
        guard += 1;
        const all = cardsOf();
        const last = all[all.length - 1];
        if (!last || last.parentNode !== el) break;
        silently(() => {
          const x = el.scrollLeft;
          el.removeChild(last);
          el.insertBefore(last, el.firstChild);
          el.scrollLeft = x + by;
        });
      }
    }

    /* ⚠️ MEASURED AT THE MOMENT IT MOVES, NOT TRUSTED FROM THE OBSERVER.
     *
     * Found on the live app, 23 September 2026: **all four rails had advanced
     * by exactly one card**, when only the rail on screen should have moved.
     *
     * The cause is the first paint. Before the photographs have landed the
     * page is short, so all four rails are inside the viewport, the observer
     * reports every one of them as visible, and four timers start. The images
     * arrive, the page grows, three rails drop below the fold — but each has
     * already taken a step. On a phone that reads as three rows quietly
     * scrolling themselves out of position while you are looking at the first.
     *
     * Al, 8 September 2026: *"if he's looking at daily deals, you can hardly
     * see happy hour, then happy hour should stop."*  An observer flag set
     * during layout is not what he is looking at now. */
    function reallyOnScreen() {
      try {
        const r = el.getBoundingClientRect();
        const h = window.innerHeight || document.documentElement.clientHeight || 0;
        const shown = Math.min(r.bottom, h) - Math.max(r.top, 0);
        return r.height > 0 && shown / r.height >= 0.5;
      } catch { return false; }
    }

    function drift() {
      if (stopped || document.hidden || !onScreen) return;
      if (!reallyOnScreen()) return;
      const by = step();
      selfMove(() => {
        try { el.scrollTo({ left: el.scrollLeft + by, behavior: 'smooth' }); }
        catch { el.scrollLeft += by; }
      });
    }

    /* ⚠️ THE CLOCK RESTARTS ON ARRIVAL, so a row never slides the moment you
     * reach it — without this, scrolling down makes each row twitch as it
     * lands. */
    function watch(on) {
      if (on === onScreen) return;
      onScreen = on;
      if (timer) { clearInterval(timer); timer = null; }
      if (on && !stopped) timer = setInterval(drift, EVERY_MS);
    }

    let io = null;
    if (window.IntersectionObserver) {
      io = new window.IntersectionObserver((entries) => {
        for (const e of entries) watch(e.isIntersecting);
      }, { threshold: 0.5 });
      io.observe(el);
    } else {
      onScreen = true;
      timer = setInterval(drift, EVERY_MS);
    }

    /* ⚠️⚠️ A FINGER ON A ROW IS NOT A DECISION ABOUT THAT ROW. Stopping on
     * touchstart killed every rail a downward scroll passed over, permanently.
     * What means "I have taken over" is moving the rail SIDEWAYS. */
    /* ⚠️⚠️ DECLARED BEFORE THE CLOSURE THAT USES IT, AND THAT IS NOT TIDINESS.
     *
     * This read `const scrolled = () => { … settle … }` and then `let settle =
     * null` on the line AFTER it. It works today only because nothing calls
     * `scrolled` until the listener is attached, which is later still — but it
     * is a temporal-dead-zone waiting for someone to move one line, and a TDZ
     * in a minified build reads as **"Cannot access 'a' before
     * initialization"**, which is the exact fault Al has been staring at all
     * evening on his phone.
     *
     * I could not prove this was his fault and I could not prove it was not:
     * it is the only construct of that shape in anything I have written this
     * week. A latent one is not worth keeping to find out. */
    let settle = null;

    const down = () => { held = true; };
    const wheel = (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) stop(); };
    const scrolled = () => {
      const x = el.scrollLeft;
      if (held && selfMoving === 0 && Math.abs(x - wasX) > 6) stop();
      wasX = x;
      sync();
      if (settle) clearTimeout(settle);
      settle = setTimeout(keepFed, 120);
    };

    /* ⚠️⚠️⚠️ THE FINGER LIFTS WHERE IT LIKES — LISTEN ON THE DOCUMENT.
     * Al, 9 September 2026: *"why does last minute special stick there. we had
     * many others that should scroll."*  `held` went true on pointerdown on
     * the wrap and was only cleared by pointerup on the wrap — but scrolling
     * down means dragging the finger OFF that row before lifting, so the wrap
     * never saw the release and that one rail stayed armed for the session.
     * Down belongs to the element; up belongs to the document. */
    const up = () => { setTimeout(() => { held = false; }, 60); };

    box.addEventListener('pointerdown', down, { passive: true });
    box.addEventListener('touchstart', down, { passive: true });
    box.addEventListener('wheel', wheel, { passive: true });
    el.addEventListener('scroll', scrolled, { passive: true });
    for (const ev of ['pointerup', 'touchend', 'touchcancel', 'pointercancel']) {
      document.addEventListener(ev, up, { passive: true });
    }
    /* ⚠️ A BELT FOR THE BRACES — a rail cannot be "being held" while the page
     * has no pointer down on it at all. */
    const loose = () => { held = false; };
    document.addEventListener('visibilitychange', loose);
    window.addEventListener('blur', loose);

    sync();
    const settled = setTimeout(sync, 400);   /* again once the photographs land */

    return () => {
      if (timer) clearInterval(timer);
      if (settle) clearTimeout(settle);
      clearTimeout(settled);
      if (io) io.disconnect();
      box.removeEventListener('pointerdown', down);
      box.removeEventListener('touchstart', down);
      box.removeEventListener('wheel', wheel);
      el.removeEventListener('scroll', scrolled);
      for (const ev of ['pointerup', 'touchend', 'touchcancel', 'pointercancel']) {
        document.removeEventListener(ev, up);
      }
      document.removeEventListener('visibilitychange', loose);
      window.removeEventListener('blur', loose);
    };
  }, []);

  function slide(dir) {
    const el = rail.current;
    if (!el) return;
    const card = el.querySelector('.card');
    const by = dir * (card ? card.offsetWidth + CARD_GAP : Math.round(el.clientWidth * 0.8));
    try { el.scrollBy({ left: by, behavior: 'smooth' }); }
    catch { el.scrollLeft += by; }
  }

  return (
    <div className="carousel-wrap" ref={wrap}>
      {/* ⚠️ AN ARROW AT THE END OF ITS TRAVEL FADES OUT rather than sitting
          there doing nothing — the prototype's own rule. On a phone neither
          shows; the half-peeking next card is the instruction. */}
      <button type="button" className="car-btn prev" aria-label="Previous"
        style={{ opacity: canPrev ? 1 : 0, pointerEvents: canPrev ? 'auto' : 'none' }}
        onClick={() => slide(-1)}>‹</button>
      <div className="carousel" ref={rail}>
        {children}
        {endcard}
      </div>
      <button type="button" className="car-btn next" aria-label="Next"
        style={{ opacity: canNext ? 1 : 0, pointerEvents: canNext ? 'auto' : 'none' }}
        onClick={() => slide(1)}>›</button>
    </div>
  );
}
